import React, { useState, useEffect, useCallback } from "react";
import { api } from "./api";
import { useAuth } from "./AuthContext";
import { Icon } from "./Icons";

// Design system allineato a Impostazioni
const C = {
  blue: "#1565C0", grey: "#546E7A", light: "#F5F5F5", border: "#E0E0E0",
  text: "#212121", textSub: "#757575", error: "#B71C1C", errorLight: "#FFEBEE",
  bluLight: "#E3F2FD",
};
const FONT = "Roboto, 'Segoe UI', Arial, sans-serif";
const FONT_SIZE = 15;
const ROW_H = 44;
const RUOLI = ["admin", "compilatore", "visualizzatore"];

const overlayStyle = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modaleStyle = { background: "#fff", borderRadius: 10, padding: 28, minWidth: 380, boxShadow: "0 8px 30px rgba(0,0,0,0.22)", display: "flex", flexDirection: "column", gap: 14, fontFamily: FONT };
const labelStyle = { fontSize: 12, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 5 };
const inputStyle = { border: `1.5px solid ${C.border}`, borderRadius: 6, padding: "9px 12px", fontSize: FONT_SIZE, width: "100%", boxSizing: "border-box", outline: "none", fontFamily: FONT, color: C.text };
const btnPrimary = { background: C.blue, color: "#fff", border: "none", borderRadius: 6, padding: "9px 20px", cursor: "pointer", fontWeight: 700, fontSize: FONT_SIZE, fontFamily: FONT };
const btnSecondary = { background: C.light, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: "9px 20px", cursor: "pointer", fontWeight: 600, fontSize: FONT_SIZE, fontFamily: FONT };
const thStyle = { padding: "10px 14px", fontWeight: 700, textAlign: "left", borderBottom: `2px solid ${C.border}`, color: C.grey, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5, background: C.light };
const tdStyle = { padding: "0 14px", height: ROW_H, borderBottom: `1px solid ${C.border}`, verticalAlign: "middle", fontSize: FONT_SIZE, color: C.text };
const iconBtn = { background: "none", border: `1px solid ${C.border}`, borderRadius: 5, width: 32, height: 32, cursor: "pointer", fontSize: 14, color: C.grey, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0 };

const badgeRuolo = (ruolo) => {
  const col = ruolo === "admin" ? C.error : ruolo === "compilatore" ? C.blue : C.grey;
  return (
    <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700, color: "#fff", background: col }}>
      {ruolo}
    </span>
  );
};

// ── Modale utente ──────────────────────────────────────────────────────────────
function ModaleUtente({ utente, onClose, onSave }) {
  const nuovo = !utente;
  const [username, setUsername] = useState(utente?.username ?? "");
  const [ruolo, setRuolo]       = useState(utente?.ruolo ?? "visualizzatore");
  const [attivo, setAttivo]     = useState(utente ? utente.attivo === 1 : true);
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState("");

  const submit = async () => {
    if (!username.trim()) { setErr("Username obbligatorio"); return; }
    if (nuovo && !password.trim()) { setErr("Password obbligatoria"); return; }
    setLoading(true);
    try {
      if (nuovo) {
        await onSave(null, { username: username.trim(), password, ruolo, attivo: attivo ? 1 : 0 });
      } else {
        const patch = { username: username.trim(), ruolo, attivo: attivo ? 1 : 0 };
        if (password.trim()) patch.password = password;
        await onSave(utente.id, patch);
      }
      onClose();
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };

  return (
    <div style={overlayStyle}>
      <div style={modaleStyle}>
        <h3 style={{ margin: 0, color: C.text }}>{nuovo ? "Nuovo utente" : `Modifica ${utente.username}`}</h3>
        <div>
          <label style={labelStyle}>Username</label>
          <input style={inputStyle} value={username} onChange={e => setUsername(e.target.value)} autoFocus autoComplete="off" />
        </div>
        <div>
          <label style={labelStyle}>Ruolo</label>
          <select style={inputStyle} value={ruolo} onChange={e => setRuolo(e.target.value)}>
            {RUOLI.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>{nuovo ? "Password" : "Nuova password (lascia vuoto per non cambiarla)"}</label>
          <input style={inputStyle} type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: FONT_SIZE, color: C.text }}>
          <input type="checkbox" checked={attivo} onChange={e => setAttivo(e.target.checked)} />
          Utente attivo
        </label>
        {err && <div style={{ background: C.errorLight, color: C.error, borderRadius: 6, padding: "8px 12px", fontSize: 13, fontWeight: 600 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
          <button onClick={onClose} style={btnSecondary}>Annulla</button>
          <button onClick={submit} disabled={loading} style={btnPrimary}>{loading ? "..." : "Salva"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Componente: Gestione utenti + Log attività ─────────────────────────────────
export default function GestioneUtenti({ view }) {
  const { utente: corrente } = useAuth();
  const showUtenti = !view || view === "utenti";
  const showLog = !view || view === "log";
  const [utenti, setUtenti]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [modale, setModale]   = useState(null); // 'nuovo' | utente

  const [log, setLog]         = useState([]);
  const [logLoading, setLogLoading] = useState(true);

  const loadUtenti = useCallback(async () => {
    setLoading(true);
    try { setUtenti(await api.getUtenti()); setError(""); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  const loadLog = useCallback(async () => {
    setLogLoading(true);
    try { setLog(await api.getLog()); }
    catch (e) { setError(e.message); }
    finally { setLogLoading(false); }
  }, []);

  useEffect(() => { loadUtenti(); loadLog(); }, [loadUtenti, loadLog]);

  const handleSave = async (id, data) => {
    if (id == null) await api.createUtente(data);
    else await api.updateUtente(id, data);
    await loadUtenti();
    await loadLog();
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Eliminare l'utente "${u.username}"?`)) return;
    try { await api.deleteUtente(u.id); await loadUtenti(); await loadLog(); }
    catch (e) { setError(e.message); }
  };

  const fmtTs = (ts) => {
    try { return new Date(ts).toLocaleString("it-IT"); } catch { return ts; }
  };

  return (
    <>
      {error && (
        <div style={{ background: C.errorLight, border: "1px solid #EF9A9A", color: C.error, padding: "10px 14px", borderRadius: 6, marginBottom: 16, fontSize: FONT_SIZE, fontWeight: 600, display: "flex", alignItems: "center" }}>
          <Icon name="warning" size={16} color={C.error} style={{ marginRight: 6 }} />{error}
          <button onClick={() => setError("")} style={{ marginLeft: "auto", cursor: "pointer", background: "none", border: "none", fontWeight: 700, fontSize: 18, color: C.error }}>×</button>
        </div>
      )}

      {showUtenti && (
      <div style={{ background: "#fff", borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: `1px solid ${C.border}`, overflow: "hidden", marginBottom: 24 }}>
        <div style={{ background: C.grey, color: "#fff", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, fontFamily: FONT }}>Gestione utenti</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2, fontFamily: FONT }}>{utenti.length} utenti</div>
          </div>
          <button onClick={() => setModale("nuovo")} style={{ background: "rgba(255,255,255,0.18)", border: "1.5px solid rgba(255,255,255,0.5)", color: "#fff", borderRadius: 6, padding: "8px 18px", cursor: "pointer", fontWeight: 700, fontSize: FONT_SIZE, fontFamily: FONT }}>
            + Aggiungi
          </button>
        </div>
        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: C.textSub, fontFamily: FONT }}>Caricamento...</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: FONT_SIZE, fontFamily: FONT }}>
            <thead>
              <tr>
                <th style={thStyle}>Username</th>
                <th style={{ ...thStyle, width: 150 }}>Ruolo</th>
                <th style={{ ...thStyle, width: 100, textAlign: "center" }}>Stato</th>
                <th style={{ ...thStyle, width: 120, textAlign: "center" }}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {utenti.map(u => (
                <tr key={u.id}
                  onMouseEnter={e => e.currentTarget.style.background = C.bluLight}
                  onMouseLeave={e => e.currentTarget.style.background = ""}
                >
                  <td style={{ ...tdStyle, fontWeight: 600 }}>
                    {u.username}
                    {corrente?.username === u.username && <span style={{ fontSize: 11, color: C.textSub, marginLeft: 6 }}>(tu)</span>}
                  </td>
                  <td style={tdStyle}>{badgeRuolo(u.ruolo)}</td>
                  <td style={{ ...tdStyle, textAlign: "center", color: u.attivo === 1 ? C.text : C.textSub }}>
                    {u.attivo === 1 ? "attivo" : "disattivo"}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <button onClick={() => setModale(u)} title="Modifica" style={{ ...iconBtn, marginRight: 6 }}><Icon name="edit" size={15} color={C.grey} /></button>
                    <button onClick={() => handleDelete(u)} title="Elimina" style={{ ...iconBtn, color: C.error, borderColor: "#EF9A9A" }}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}

      {showLog && (
      <div style={{ background: "#fff", borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: `1px solid ${C.border}`, overflow: "hidden", marginBottom: 24 }}>
        <div style={{ background: C.grey, color: "#fff", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, fontFamily: FONT }}>Log attività</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2, fontFamily: FONT }}>Ultime {log.length} scritture registrate</div>
          </div>
          <button onClick={loadLog} style={{ background: "rgba(255,255,255,0.18)", border: "1.5px solid rgba(255,255,255,0.5)", color: "#fff", borderRadius: 6, padding: "8px 18px", cursor: "pointer", fontWeight: 700, fontSize: FONT_SIZE, fontFamily: FONT, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Icon name="refresh" size={16} color="#fff" /> Aggiorna
          </button>
        </div>
        {logLoading ? (
          <div style={{ padding: 32, textAlign: "center", color: C.textSub, fontFamily: FONT }}>Caricamento...</div>
        ) : log.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: C.textSub, fontFamily: FONT }}>Nessuna attività registrata.</div>
        ) : (
          <div style={{ maxHeight: 420, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, fontFamily: FONT }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, position: "sticky", top: 0, width: 175 }}>Data / ora</th>
                  <th style={{ ...thStyle, position: "sticky", top: 0, width: 140 }}>Utente</th>
                  <th style={{ ...thStyle, position: "sticky", top: 0, width: 80 }}>Azione</th>
                  <th style={{ ...thStyle, position: "sticky", top: 0 }}>Risorsa</th>
                  <th style={{ ...thStyle, position: "sticky", top: 0, width: 70, textAlign: "center" }}>Esito</th>
                </tr>
              </thead>
              <tbody>
                {log.map(r => (
                  <tr key={r.id}>
                    <td style={{ ...tdStyle, height: 34, whiteSpace: "nowrap" }}>{fmtTs(r.timestamp)}</td>
                    <td style={{ ...tdStyle, height: 34, fontWeight: 600 }}>{r.username || "—"}</td>
                    <td style={{ ...tdStyle, height: 34 }}>{r.metodo}</td>
                    <td style={{ ...tdStyle, height: 34, color: C.textSub, fontFamily: "monospace", fontSize: 13 }}>{r.path}</td>
                    <td style={{ ...tdStyle, height: 34, textAlign: "center", color: r.esito < 400 ? C.text : C.error }}>{r.esito}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {modale && (
        <ModaleUtente
          utente={modale === "nuovo" ? null : modale}
          onClose={() => setModale(null)}
          onSave={handleSave}
        />
      )}
    </>
  );
}
